import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { Application, Role } from '../lib/types';
import { Badge, Card, Empty, Field, Modal, Notice, PageHead } from '../components/ui';

export function Roles() {
  const roles = useAsync(() => api.get<Role[]>('/api/admin/roles'));
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHead
        title="Roles"
        description="Roles bundle permissions and normally hang off an Entra group. Whoever joins the group has the permissions at their next sign-in - joining and leaving ride along with your existing directory process. A global role counts everywhere; a role bound to an application counts only for people acting through it."
        actions={
          <button className="primary" onClick={() => setCreating(true)}>
            Create role
          </button>
        }
      />

      {roles.error ? <Notice tone="danger">{roles.error}</Notice> : null}

      <Card tight>
        {roles.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Description</th>
                <th>Entra groups</th>
                <th className="num">Members</th>
              </tr>
            </thead>
            <tbody>
              {roles.data.map((role) => (
                <tr key={role.id}>
                  <td>
                    <Link to={`/roles/${role.id}`}>{role.name}</Link>
                  </td>
                  <td>
                    {role.application_id ? (
                      <Link to={`/applications/${role.application_id}`}>{role.application_name}</Link>
                    ) : (
                      <Badge>Global</Badge>
                    )}
                  </td>
                  <td className="muted">{role.description ?? '—'}</td>
                  <td>
                    {role.groups.length ? (
                      <div className="row">
                        {role.groups.map((group) => (
                          <Badge key={group.id}>{group.group_name ?? group.entra_group_id}</Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="muted">assigned manually only</span>
                    )}
                  </td>
                  <td className="num">{role.memberCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No role created yet.</Empty>
        )}
      </Card>

      {creating ? (
        <CreateRoleDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            roles.reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * With `applicationId` the scope is fixed (the application page creates its
 * own roles); without it the dialog offers the choice, global by default.
 */
export function CreateRoleDialog({
  onClose,
  onCreated,
  applicationId: fixedApplicationId,
}: {
  onClose: () => void;
  onCreated: () => void;
  applicationId?: string;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [applicationId, setApplicationId] = useState(fixedApplicationId ?? '');
  const [error, setError] = useState<string | null>(null);
  const applications = useAsync(
    () => (fixedApplicationId ? Promise.resolve([]) : api.get<Application[]>('/api/applications')),
    [fixedApplicationId],
  );

  async function submit(): Promise<void> {
    try {
      await api.post('/api/admin/roles', {
        name,
        description: description || undefined,
        applicationId: applicationId || undefined,
      });
      onCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Create role"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={!name}>
            Create
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Name">
        <input type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="Support basic" />
      </Field>
      <Field label="Description" hint="optional">
        <input type="text" value={description} onChange={(event) => setDescription(event.target.value)} />
      </Field>
      {fixedApplicationId ? null : (
        <Field label="Scope" hint="a bound role counts only for people acting through that application">
          <select value={applicationId} onChange={(event) => setApplicationId(event.target.value)}>
            <option value="">Global</option>
            {(applications.data ?? []).map((application) => (
              <option key={application.id} value={application.id}>
                {application.name}
              </option>
            ))}
          </select>
        </Field>
      )}
    </Modal>
  );
}
